import * as React from "react";

import { useMutation } from "react-query";
import * as Redux from "react-redux";

import { useLocalStorageState } from "client/hooks";
import { logAmplitudeEvent, setAmplitudeUserId } from "client/lib/amplitude";
import { UserService } from "client/services";
import { errorActions, userActions } from "client/store";
import { UserStateResponse } from "client/types";
import { getEmail, getEmailFromUrlParams } from "client/utils";

import { useMerchantModel } from "./useMerchantModel";

import { useBranding } from ".";

type UseLeadResult = {
  isLoading: boolean;
  handleSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
};

type RequestArgs = {
  saveData: any;
  signupData: any;
};

const submitLead = async ({
  saveData,
  signupData,
}: RequestArgs): Promise<UserStateResponse> => {
  const signupRes = await UserService.signupMerchant(signupData);

  // If userstate isn't POST_LEAD it means we don't
  // support the business and will redirect the merchant
  // Also means that we don't want to save as the prospect id
  // isn't on the 'g' object and will throw a 401 error
  if (signupRes.userState === "POST_LEAD") {
    await UserService.saveMerchant(saveData);
  }

  return signupRes;
};

export function useLead(
  onValidationErrorCb?: (errors: Record<string, any>) => void
): UseLeadResult {
  const dispatch = Redux.useDispatch();
  const {
    merchant,
    formatMerchantForSignup,
    syncStoreWithMerchant,
    getFieldSetUpdater,
    formatMerchantForSave,
  } = useMerchantModel();
  // @ts-ignore
  const [_, setHasSubmittedLead] = useLocalStorageState(
    "hasSubmittedLead",
    false
  );

  const { mutate: completeSignup, isLoading } = useMutation(submitLead, {
    onError: (err: Error) => {
      dispatch(errorActions.setError(err.message));
    },
    onSuccess: ({ userState, validation_errors, mlsProspectRef }) => {
      if (validation_errors) {
        dispatch(
          errorActions.setError(
            "An error occurred with a field, please check all fields and re-submit"
          )
        );
        dispatch(errorActions.setFieldErrorMap(validation_errors));
        if (onValidationErrorCb) {
          onValidationErrorCb(validation_errors);
        }
      } else {
        setHasSubmittedLead(true);
        logAmplitudeEvent("REDESIGN_SIGN_UP", "submitted_pre_lead_form");
        dispatch(userActions.setUserState(userState));

        // We want to track when prospect id is set for merchant after lead form submission
        // mlsProspectRef is restored to FE when we are routed to CompanyInfoForm however adding a log to the form on restore will fire at the beginning of each user session (everytime user logs back in)
        if (mlsProspectRef) {
          setTimeout(() => {
            logAmplitudeEvent("REDESIGN_SIGN_UP", "created_lead", {
              mlsProspectRef,
            });
          }, 1000);
        }

        /**
         * MLS does _not_ have the concept of multiple "fieldsets". Instead,
         *   they have concrete models that act as the store of record.
         *
         * We do not have that idea - instead we have fields that can show up
         *   in multiple fieldsets. It's a business rule in MOP UI that says e.g.
         *   "the first / last name from lead form should synced with first owner"
         *
         * As a result, this method is called to sync values from Lead Fieldset to
         *   the rest of MOPs FieldSets.
         */
        syncStoreWithMerchant();
      }
    },
  });

  const { brandTitle } = useBranding();
  React.useEffect(() => {
    logAmplitudeEvent("REDESIGN_SIGN_UP", "loaded_leadform", {});

    if (brandTitle === "Clearpay") {
      getFieldSetUpdater(merchant.lead)(
        merchant.companyInformation.registeredCountry,
        "United Kingdom"
      );
    }
    const email = getEmailFromUrlParams() || getEmail(merchant);
    if (email) {
      dispatch(userActions.setEmail(email));
      setAmplitudeUserId(email);
    }
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const signupData = formatMerchantForSignup();
    const saveData = formatMerchantForSave();
    completeSignup({ signupData, saveData });
  };

  return {
    isLoading,
    handleSubmit,
  };
}
